import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { formatMetaRecommendationPoints, metaDeliveryTooltip } from '../metaDelivery';
import { useMetaTooltipProps } from './metaTooltipBoundary';
import { getMetaAdsDeliveryDotSx, getMetaAdsStatusPillSx, metaAdsBodyTextSx, metaAdsMutedTextSx } from './metaAdsUi.styles';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Punto de color de la entrega junto al nombre (verde / ámbar / rojo / gris, de
 * `delivery.dot`). `aria-hidden`: el texto lo lleva la píldora.
 */
export function MetaDeliveryDot({ delivery, testId }) {
  // ACTIVE_NO_DELIVERY (29/10): encendida en Meta → verde, como las activas.
  const dot = isMetaDeliveryNoRecent(delivery) ? 'green' : (delivery?.dot || 'gray');
  return <Box component="span" aria-hidden="true" data-dot={dot} data-testid={testId} sx={getMetaAdsDeliveryDotSx(dot)} />;
}

/**
 * ACTIVE_NO_DELIVERY («Activa (sin entrega reciente)»): encendida en Meta pero sin impresiones
 * hoy ni ayer. Por código de estado (no por el texto). Desde el 29/10 se trata como activa:
 * punto verde y, en lugar de la píldora naranja, el texto gris «Sin mostrarse hoy ni ayer».
 */
export const isMetaDeliveryNoRecent = (delivery) => String(delivery?.status || '').trim().toUpperCase() === 'ACTIVE_NO_DELIVERY';
export const META_NO_RECENT_LABEL = 'Sin mostrarse hoy ni ayer';
export const META_NO_RECENT_HINT = 'Está encendida en Meta pero no se mostró hoy ni ayer.';

/**
 * La píldora sería «Activa» (o «Activo», etiqueta vieja de `effective_status`) con punto verde
 * (ACTIVE_DELIVERING / tono success / ACTIVE viejo; ver `metaDeliveryDot`). Cualquier otra
 * etiqueta (aunque el punto sea verde) sigue como píldora.
 */
export const isMetaDeliveryActive = (delivery) => isMetaDeliveryNoRecent(delivery)
  || (delivery?.dot === 'green' && /^activ[ao]$/i.test(String(delivery?.label || '').trim()));
/** Hay que dibujar el estado al lado (píldora, o «Sin mostrarse hoy ni ayer»): todo menos «Activa» a secas. */
export const metaDeliveryShowsStatus = (delivery) => !isMetaDeliveryActive(delivery) || isMetaDeliveryNoRecent(delivery);
export const META_ACTIVE_LABEL = 'Activa';

/**
 * Punto de entrega que reemplaza la píldora «Activa» en la lista de Campañas (paso 2): verde
 * con Tooltip y `aria-label` «Activa» (no solo color). Otro estado → el punto de siempre
 * (`aria-hidden`, la píldora lleva el texto).
 */
export function MetaActiveDot({ delivery, testId }) {
  const boundaryProps = useMetaTooltipProps();
  if (!isMetaDeliveryActive(delivery)) return <MetaDeliveryDot delivery={delivery} testId={testId} />;
  // Sin entrega reciente: el detalle va en el Tooltip del texto «Sin mostrarse…», no se repite acá.
  const tip = isMetaDeliveryNoRecent(delivery) ? '' : metaDeliveryTooltip(delivery);
  return (
    <Tooltip title={<span>{tip ? `${META_ACTIVE_LABEL} · ${tip}` : META_ACTIVE_LABEL}</span>} placement="top" arrow {...boundaryProps}>
      <Box component="span" role="img" aria-label={META_ACTIVE_LABEL} data-dot="green" data-testid={testId} sx={getMetaAdsDeliveryDotSx('green')} />
    </Tooltip>
  );
}

/**
 * Píldora de entrega (Ajuste 6): `delivery_label` con color por `delivery_tone`
 * y `delivery_hint` en Tooltip de MUI (nunca `title` nativo: quedaba flotando
 * sobre el mapa). El título va como nodo: con string + `describeChild` MUI vuelve
 * a poner `title` en el elemento.
 *
 * @param {{ delivery: object|null, label?: string|null, testId?: string }} props
 */
export function MetaDeliveryPill({ delivery, label = null, testId }) {
  const boundaryProps = useMetaTooltipProps();
  if (isMetaDeliveryNoRecent(delivery)) return <MetaNoRecentNote delivery={delivery} testId={testId} />;
  const text = delivery?.label || label;
  if (!text) return null;
  const pill = (
    <Box component="span" sx={getMetaAdsStatusPillSx(delivery?.pillTone || 'pending')} data-testid={testId}>
      {text}
    </Box>
  );
  const tip = metaDeliveryTooltip(delivery);
  if (!tip) return pill;
  return (
    <Tooltip title={<span>{tip}</span>} placement="top" arrow describeChild {...boundaryProps}>
      {pill}
    </Tooltip>
  );
}

const noRecentSx = { ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '18px', cursor: 'default', borderRadius: '4px', '&:focus-visible': { outline: '2px solid', outlineOffset: 1 } };

/**
 * ACTIVE_NO_DELIVERY: texto secundario gris (sin píldora), con Tooltip = `delivery_hint` de la
 * API (fallback: «Está encendida en Meta pero no se mostró hoy ni ayer.»).
 */
export function MetaNoRecentNote({ delivery, testId }) {
  const boundaryProps = useMetaTooltipProps();
  const tip = metaDeliveryTooltip(delivery) || META_NO_RECENT_HINT;
  return (
    <Tooltip title={<span>{tip}</span>} placement="top" arrow describeChild {...boundaryProps}>
      <Box component="span" tabIndex={0} data-no-recent="true" data-testid={testId} sx={noRecentSx}>
        {META_NO_RECENT_LABEL}
      </Box>
    </Tooltip>
  );
}

/**
 * Lista de recomendaciones dentro de Terrio (MUI Dialog con el chrome de los
 * modales de la app). Por cada una: título en negrita, mensaje, impacto y puntos;
 * si vienen de varios niveles, de dónde («Campaña», «Anuncio «X»»). `meta_url` solo
 * acá, como «Aplicar en Meta» (pestaña nueva, solo https facebook.com).
 *
 * @param {{ open: boolean, onClose: () => void, title: string, summary: object }} props
 */
export function MetaRecommendationsDialog({ open, onClose, title, summary }) {
  const entries = summary?.entries || [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...appModalPaperSx, width: 'min(520px, 95vw)', maxWidth: '520px', height: 'auto', maxHeight: 'min(90vh, 680px)' } }}
      aria-labelledby="meta-recommendations-title"
    >
      <DialogTitle id="meta-recommendations-title" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>{plural(summary?.count || 0, 'Recomendación de Meta', 'Recomendaciones de Meta')}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-recommendations-dialog">
        {title ? <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</Typography> : null}
        <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }}>
          Las sugiere Meta y se aplican desde su Administrador de anuncios.
        </Typography>
        {entries.length === 0 ? (
          <Typography sx={metaAdsMutedTextSx}>Meta no mandó el detalle de estas recomendaciones.</Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {entries.map((rec) => {
              const points = formatMetaRecommendationPoints(rec.points);
              return (
                <Box
                  component="li"
                  key={rec.key}
                  data-testid="meta-recommendation"
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)', '&:first-of-type': { borderTop: 'none', pt: 0 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 700, overflowWrap: 'anywhere' }}>{rec.title}</Typography>
                    {points ? <Box component="span" sx={getMetaAdsStatusPillSx('partial')}>{points}</Box> : null}
                  </Box>
                  {rec.message ? <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px' }}>{rec.message}</Typography> : null}
                  {rec.estimatedImpact ? <Typography sx={metaAdsMutedTextSx}>{rec.estimatedImpact}</Typography> : null}
                  {summary.multipleSources && rec.sources.length ? (
                    <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }}>{rec.sources.join(' · ')}</Typography>
                  ) : null}
                  {rec.metaUrl ? (
                    <Button
                      size="small"
                      component="a"
                      href={rec.metaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />}
                      sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 1, minWidth: 0 }}
                    >
                      Aplicar en Meta
                    </Button>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Chip «N recomendaciones» (solo si hay) que abre la lista dentro de Terrio. Un
 * solo chip por campaña (o por anuncio en la ficha): `summary` de
 * `collectMetaRecommendations`.
 *
 * @param {{ summary: object|null, title?: string, testId?: string }} props
 */
export function MetaRecommendations({ summary, title = '', testId }) {
  const [open, setOpen] = useState(false);
  const n = summary?.count || 0;
  if (n <= 0) return null;
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-testid={testId}
        sx={{
          ...getMetaAdsStatusPillSx('partial'),
          border: 'none',
          cursor: 'pointer',
          '&:hover': { filter: 'brightness(1.2)' },
          '&:focus-visible': { outline: '2px solid', outlineOffset: 2 },
        }}
      >
        {plural(n, 'recomendación', 'recomendaciones')}
      </Box>
      <MetaRecommendationsDialog open={open} onClose={() => setOpen(false)} title={title} summary={summary} />
    </>
  );
}

/**
 * Nota discreta de cobertura (`data_coverage.complete === false` en
 * `getcampaignbreakdowns`). No es un error: texto chico, sin color de alerta.
 *
 * @param {{ coverage: { note: string }|null, testId?: string }} props
 */
export function MetaCoverageNote({ coverage, testId = 'meta-coverage-note' }) {
  if (!coverage?.note) return null;
  return (
    <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} data-testid={testId}>
      {coverage.note}
    </Typography>
  );
}
